import './connectNewWalletsToPairModal.scss';

import React, { useCallback, useState } from 'react';

import { ApiPairs } from 'api';
import { useTypedDispatch } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { ConnectWalletToPairTable } from 'tables';
import { EDexBot } from 'types/bots';
import { IConnectWalletToPairRow } from 'types/tables/dex/connectWalletToPairRow';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { ErrorText } from 'ui/ErrorText/ErrorText';
import { ConnectNewWalletsToPairForm } from 'ui/forms';
import { Modal } from 'ui/Modal/Modal';

interface IConnectNewWalletsToPairModalProps {
  onClose: () => void;
  onOpen: () => void;
  onSuccess: () => void;
  pairId: number;
}

const ConnectNewWalletsToPairModal: React.FC<IConnectNewWalletsToPairModalProps> = ({
  onClose,
  onOpen,
  onSuccess,
  pairId,
}) => {
  const dispatch = useTypedDispatch();
  const [validateError, setValidateError] = useState<string | null>(null);
  const [requestLoading, setRequestLoading] = useState<boolean>(false);

  const [selectedRecords, setSelectedRecords] = useState<IConnectWalletToPairRow[]>([]);

  const handleConnectNewWallets = useCallback(async () => {
    if (selectedRecords.length === 0) {
      setValidateError('Select at least one wallet');
      return;
    }

    try {
      setRequestLoading(true);

      const { errorMessage, isSuccess } = await ApiPairs.addWalletsToPair({
        pairId,
        wallets: selectedRecords.map(selectedRow => ({
          id: Number(selectedRow.id),
          bots_ids: [
            EDexBot.anti_font_run,
            EDexBot.sl_bot,
            EDexBot.balance_bot,
            EDexBot.buy_sell_bot,
            EDexBot.liquidity_bot,
          ],
        })),
      });

      onClose();

      if (isSuccess) {
        dispatch(
          setAlertState({
            type: 'success',
            text: `You successfuly added ${selectedRecords.length} wallets to pair`,
            onClose: () => {
              dispatch(dropAlertState());
              onSuccess();
            },
            onSubmit: () => {
              dispatch(dropAlertState());
              onSuccess();
            },
          }),
        );
      } else {
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: errorMessage,
            onClose: () => {
              dispatch(dropAlertState());
            },
            onSubmit: () => {
              dispatch(dropAlertState());
              onOpen();
            },
          }),
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setRequestLoading(false);
    }

    setValidateError(null);
  }, [pairId, selectedRecords, dispatch, onClose, onOpen, onSuccess]);

  return (
    <Modal onClose={onClose} title={`Connect new wallets: ${selectedRecords.length}`}>
      <ConnectNewWalletsToPairForm>
        <div className="connect-new-wallets-to-pair-modal-content">
          <ConnectWalletToPairTable setSelectedRecords={setSelectedRecords} />
        </div>
        <ErrorText>{validateError ?? undefined}</ErrorText>
        {!requestLoading && (
          <Button
            type="submit"
            onClick={handleConnectNewWallets}
            disabled={selectedRecords.length === 0}
          >
            Connect
          </Button>
        )}
        {requestLoading && <ButtonLoading />}
      </ConnectNewWalletsToPairForm>
    </Modal>
  );
};

export { ConnectNewWalletsToPairModal };
